import { Injectable } from '@angular/core';
import { FormControl, AbstractControl, ValidationErrors } from '@angular/forms';


@Injectable()

export class CustomValidation {

    private inappropriateWords: string[] = [
        // Profanity
        'damn', 'hell', 'crap', 'stupid', 'idiot', 'moron', 'fool', 'dumb', 'ass', 'bastard',
        // Abusive/Violent terms
        'hate', 'kill', 'die', 'murder', 'violence', 'attack',
        // Slang/Informal (common internet slang)
        'lol', 'omg', 'wtf', 'fml', 'smh', 'tbh', 'imo', 'btw', 'rofl', 'lmao', 'stfu',
        // Offensive terms
        'retard', 'cripple', 'gimp',
        // Add more words as per your organization's policy
    ];

    constructor() {
    }
    
    noWhitespaceValidator(control: FormControl) {
        let isWhitespace = (control.value || '').trim().length === 0;
        let isValid = !isWhitespace;
        return isValid ? null : { 'whitespace': true }
    }

    
    inappropriateLanguageValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Don't validate empty values
        }

        const value = control.value.toString().trim();
        const lowerValue = value.toLowerCase();
        
        // Check for inappropriate words (whole word matching only)
        for (const word of this.inappropriateWords) {
            // Use word boundary regex to match whole words only (prevents false positives)
            const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`\\b${escapedWord}\\b`, 'gi');
            if (regex.test(value)) {
                return {
                    inappropriateLanguage: {
                        message: `The text contains inappropriate language ("${word}"). Please use professional language.`,
                        foundWord: word
                    }
                };
            }
        }

        // Check for common patterns of profanity (character substitutions like f*ck, sh*t)
        const profanityPatterns = [
            { pattern: /f\*+ck|f\*+k|f\*ck/i, word: 'profanity' },
            { pattern: /sh\*+t|sh\*t/i, word: 'profanity' },
            { pattern: /b\*+tch|b\*tch/i, word: 'profanity' },
            { pattern: /a\*+s|a\*s/i, word: 'profanity' },
            { pattern: /d\*+mn|d\*mn/i, word: 'profanity' },
            { pattern: /h\*+ll|h\*ll/i, word: 'profanity' },
            { pattern: /c\*+nt|c\*nt/i, word: 'profanity' },
            { pattern: /p\*+ss|p\*ss/i, word: 'profanity' }
        ];

        for (const item of profanityPatterns) {
            if (item.pattern.test(value)) {
                return {
                    inappropriateLanguage: {
                        message: `The text contains inappropriate language. Please use professional language.`,
                        foundPattern: item.word
                    }
                };
            }
        }

        // Check for excessive capitalization (often indicates shouting/anger)
        // Only check if text is longer than 10 characters to avoid false positives
        if (value.length > 10) {
            const upperCaseCount = (value.match(/[A-Z]/g) || []).length;
            const upperCaseRatio = upperCaseCount / value.length;
            // Flag if more than 50% is uppercase
            if (upperCaseRatio > 0.5) {
                return {
                    inappropriateLanguage: {
                        message: `Excessive use of capital letters is not professional. Please use proper capitalization.`
                    }
                };
            }
        }

        // Check for excessive punctuation (often indicates informal communication)
        const excessivePunctuation = /[!?]{3,}/g;
        if (excessivePunctuation.test(value)) {
            return {
                inappropriateLanguage: {
                    message: `Excessive punctuation is not professional. Please use proper punctuation.`
                }
            };
        }

        // Check for repeated characters (often indicates emphasis/informality)
        const repeatedChars = /(.)\1{4,}/gi;
        if (repeatedChars.test(value)) {
            return {
                inappropriateLanguage: {
                    message: `Repeated characters are not professional. Please use proper formatting.`
                }
            };
        }

        return null; // No inappropriate language found
    }

    
    setInappropriateWords(words: string[]): void {
        this.inappropriateWords = words.map(word => word.toLowerCase().trim()).filter(word => word.length > 0);
    }

    
    addInappropriateWords(words: string[]): void {
        const lowerCaseWords = words.map(word => word.toLowerCase().trim()).filter(word => word.length > 0);
        this.inappropriateWords = [...new Set([...this.inappropriateWords, ...lowerCaseWords])]; // Remove duplicates
    }

   
    getInappropriateWords(): string[] {
        return [...this.inappropriateWords];
    }

   
    checkText(text: string): { isValid: boolean; message?: string } {
        if (!text) {
            return { isValid: true };
        }

        // Create a temporary control to use the validator
        const tempControl = new FormControl(text);
        const result = this.inappropriateLanguageValidator(tempControl);
        
        if (result) {
            return {
                isValid: false,
                message: result['inappropriateLanguage']?.message || 'Text contains inappropriate language.'
            };
        }

        return { isValid: true };
    }

}